import React, { useState } from 'react';

import type { SitrepSectionType } from 'components/DisasterGPT/hooks/useSitrepsV2';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { SitrepCardSurface } from 'components/Reports/reports.types';
import ReadOnlyReportsSectionDisplay from 'components/Reports/ReadOnlyReportsSectionDisplay';
import ReportsFileAttachments from 'components/Reports/ReportsFileAttachments';
import SitrepCardShell from 'components/Reports/SitrepCardShell';
import SitrepLayoutSwitcher, {
  type SitrepLayoutMode,
} from 'components/Reports/SitrepLayoutSwitcher';
import SitrepSectionDocumentView from 'components/Reports/SitrepSectionDocumentView';
import SitrepSectionTabs, {
  sectionsHaveCategories,
} from 'components/Reports/SitrepSectionTabs';
import useSitrepCardContentData from 'components/Reports/hooks/useSitrepCardContentData';
import useSitrepSectionSelector from 'components/Reports/hooks/useSitrepSectionSelector';

type SitrepSectionPanelProps = {
  sections: SitrepSectionType[] | undefined;
  activeSectionIndex: number | null;
  onSectionChange: (index: number) => void;
  tabSetId: string;
  activeSection: SitrepSectionType | null;
  renderSection: (section: SitrepSectionType, index: number) => React.ReactNode;
  /**
   * Controlled layout mode. When omitted, the panel manages its own layout
   * state and renders an inline switcher above the sections (used by contexts
   * without a card header, e.g. {@link ReadOnlySitrepCard}).
   */
  layoutMode?: SitrepLayoutMode;
};

export const SitrepSectionPanel: React.FC<SitrepSectionPanelProps> = ({
  sections,
  activeSectionIndex,
  onSectionChange,
  tabSetId,
  activeSection,
  renderSection,
  layoutMode: controlledLayoutMode,
}) => {
  const [internalLayoutMode, setInternalLayoutMode] =
    useState<SitrepLayoutMode>('tabs');
  const layoutMode = controlledLayoutMode ?? internalLayoutMode;

  if (!sections) return null;

  return (
    <>
      {controlledLayoutMode === undefined && (
        <div className="d-flex justify-content-end mb-2">
          <SitrepLayoutSwitcher
            value={internalLayoutMode}
            onChange={setInternalLayoutMode}
          />
        </div>
      )}

      {layoutMode === 'document' ? (
        <SitrepSectionDocumentView
          sections={sections}
          activeSectionIndex={activeSectionIndex}
          onSectionChange={onSectionChange}
          renderSection={renderSection}
          className="mb-3"
          idBase={tabSetId}
        />
      ) : (
        <>
          <SitrepSectionTabs
            sections={sections}
            activeSectionIndex={activeSectionIndex}
            onSectionChange={onSectionChange}
            renderSection={renderSection}
            className="mb-3"
            idBase={tabSetId}
          />

          {!sectionsHaveCategories(sections) && activeSection && (
            <div
              id={`${tabSetId}-panel-${activeSectionIndex}`}
              role="tabpanel"
              aria-labelledby={`${tabSetId}-tab-${activeSectionIndex}`}
              className="reports-card__section-content"
            >
              {renderSection(activeSection, activeSectionIndex ?? 0)}
            </div>
          )}
        </>
      )}
    </>
  );
};

const renderReadOnlySection = (section: SitrepSectionType) => (
  <>
    <div className="d-flex align-items-center mb-3 gap-2">
      <span className="fw-bold">{section.name}</span>
    </div>
    <ReadOnlyReportsSectionDisplay section={section} />
  </>
);

type SitrepCardContentProps = {
  sitrep: GetSitrepsResponse;
  surface: SitrepCardSurface;
  children?: React.ReactNode;
};

/**
 * Shared internal component for displaying SITREP card content within the full
 * card shell. Renders the layout switcher in the card header, next to the
 * collapse arrow.
 */
const SitrepCardContent: React.FC<SitrepCardContentProps> = ({
  sitrep,
  surface,
  children,
}) => {
  const tabSetId = React.useId();
  const cardData = useSitrepCardContentData(sitrep.id);
  const { sitrepData } = cardData;
  const { activeSection, activeSectionIndex, setActiveSectionIndex } =
    useSitrepSectionSelector(sitrepData?.sections);
  const [layoutMode, setLayoutMode] = useState<SitrepLayoutMode>('tabs');

  return (
    <SitrepCardShell
      sitrep={sitrep}
      surface={surface}
      cardData={cardData}
      headerRight={
        <SitrepLayoutSwitcher value={layoutMode} onChange={setLayoutMode} />
      }
    >
      <SitrepSectionPanel
        sections={sitrepData?.sections}
        activeSectionIndex={activeSectionIndex}
        onSectionChange={setActiveSectionIndex}
        tabSetId={tabSetId}
        activeSection={activeSection}
        renderSection={renderReadOnlySection}
        layoutMode={layoutMode}
      />

      <div className="mt-3">
        <ReportsFileAttachments
          sitrepId={sitrep.id}
          workspaceId={sitrep.incident_id}
          readOnly
        />
      </div>

      {children}
    </SitrepCardShell>
  );
};

type ReadOnlySitrepCardProps = {
  sitrep: GetSitrepsResponse;
};

/**
 * Read-only SITREP card without routing dependencies. Use this in contexts
 * without router (e.g., map popups, embeds). Renders sections directly without
 * the full card shell.
 */
export const ReadOnlySitrepCard: React.FC<ReadOnlySitrepCardProps> = ({
  sitrep,
}) => {
  const tabSetId = React.useId();
  const cardData = useSitrepCardContentData(sitrep.id);
  const { sitrepData, sectionsLoading } = cardData;
  const { activeSection, activeSectionIndex, setActiveSectionIndex } =
    useSitrepSectionSelector(sitrepData?.sections);

  if (sectionsLoading) {
    return <div className="p-3">Loading SITREP details...</div>;
  }

  return (
    <div className="reports-card__content">
      <h6 className="mb-3">
        {sitrep.status === 'Summarized' ? 'Current SITREP' : sitrep.status}
      </h6>

      <SitrepSectionPanel
        sections={sitrepData?.sections}
        activeSectionIndex={activeSectionIndex}
        onSectionChange={setActiveSectionIndex}
        tabSetId={tabSetId}
        activeSection={activeSection}
        renderSection={renderReadOnlySection}
      />

      <div className="mt-3">
        <ReportsFileAttachments
          sitrepId={sitrep.id}
          workspaceId={sitrep.incident_id}
          readOnly
        />
      </div>
    </div>
  );
};

type CurrentReportsSitrepCardProps = {
  sitrep: GetSitrepsResponse;
  /** When true, the card is rendered on the historical surface. */
  historical?: boolean;
};

const CurrentReportsSitrepCard: React.FC<CurrentReportsSitrepCardProps> = ({
  sitrep,
  historical = false,
}) => {
  const surface = historical
    ? SitrepCardSurface.Historical
    : SitrepCardSurface.Current;

  return <SitrepCardContent sitrep={sitrep} surface={surface} />;
};

export default CurrentReportsSitrepCard;
